import { PositionalAudio, AudioListener, AudioLoader, Scene, Vector3 } from 'three';
import { Body } from 'cannon-es';
import metal1 from '@assets/sounds/metal/1.mp3';
import metal2 from '@assets/sounds/metal/2.mp3';
import metal3 from '@assets/sounds/metal/3.mp3';
import glass1 from '@assets/sounds/glass/glass01.wav';
import glass2 from '@assets/sounds/glass/glass02.wav';
import glass3 from '@assets/sounds/glass/glass03.wav';
import metalWood from '@assets/sounds/wood-metal.wav';
import { autoInjectable } from 'tsyringe';

type MaterialType = 'metal' | 'glass' | 'wood' | 'plastic' | 'ceramic' | 'stone' | 'rubber';

interface CollisionSoundConfig {
    material1: MaterialType;
    material2: MaterialType;
    soundPath: string;
    volume?: number;
}

interface BodyMetadata {
    materialType: MaterialType;
    soundEnabled?: boolean;
}

@autoInjectable()
export default class CollisionSoundManager {
    private audioListener: AudioListener;
    private scene: Scene;
    private audioLoader: AudioLoader;
    private soundBuffers: Map<string, AudioBuffer> = new Map();
    private audioPool: Map<string, PositionalAudio[]> = new Map();
    private lastCollisionTime: Map<string, number> = new Map();

    private readonly COLLISION_COOLDOWN = 200;

    private readonly MIN_IMPULSE = 0.01;

    private readonly MAX_CONCURRENT_SOUNDS = 8;

    private readonly MAX_SOUNDS_PER_SECOND = 15;
    private soundsPlayedThisSecond: number = 0;
    private lastSecondTimestamp: number = 0;

    private activeSounds: Set<PositionalAudio> = new Set();
    private cleanupInterval: number | null = null;

    private audioContextReady: boolean = false;
    private userInteractionReceived: boolean = false;

    private soundConfigs: CollisionSoundConfig[] = [
        { material1: 'metal', material2: 'metal', soundPath: metal1, volume: 0.6 },
        { material1: 'metal', material2: 'wood', soundPath: metalWood, volume: 0.5 },
        { material1: 'metal', material2: 'glass', soundPath: metal2, volume: 0.4 },
        { material1: 'metal', material2: 'ceramic', soundPath: glass1, volume: 0.5 },
        { material1: 'glass', material2: 'wood', soundPath: glass2, volume: 0.4 },
        { material1: 'glass', material2: 'glass', soundPath: glass3, volume: 0.3 },
        { material1: 'ceramic', material2: 'wood', soundPath: glass3, volume: 0.4 },
        { material1: 'metal', material2: 'stone', soundPath: metal2, volume: 0.5 },
        { material1: 'glass', material2: 'stone', soundPath: metalWood, volume: 0.4 },
        { material1: 'wood', material2: 'wood', soundPath: metalWood, volume: 0.4 },
        { material1: 'rubber', material2: 'metal', soundPath: metal2, volume: 0.5 },
        { material1: 'rubber', material2: 'wood', soundPath: metalWood, volume: 0.4 },
        { material1: 'rubber', material2: 'rubber', soundPath: metalWood, volume: 0.3 },
    ];

    public constructor(audioListener: AudioListener, scene: Scene) {
        this.audioListener = audioListener;
        this.scene = scene;
        this.audioLoader = new AudioLoader();

        this.cleanupInterval = window.setInterval(() => {
            this.cleanupFinishedSounds();
        }, 2000);

        this.setupUserInteractionListener();
    }

    private setupUserInteractionListener(): void {
        const unlockAudio = async () => {
            if (this.userInteractionReceived) return;

            try {
                const audioContext = this.audioListener.context;

                if (audioContext.state === 'suspended') {
                    await audioContext.resume();
                    console.log('🔓 AudioContext resumed after user interaction');
                }

                this.audioContextReady = true;
                this.userInteractionReceived = true;

                document.removeEventListener('click', unlockAudio);
                document.removeEventListener('touchstart', unlockAudio);
                document.removeEventListener('keydown', unlockAudio);

                this.playTestSound();
            } catch (error) {
                console.warn('⚠️ Failed to resume AudioContext:', error);
            }
        };

        document.addEventListener('click', unlockAudio, { once: true });
        document.addEventListener('touchstart', unlockAudio, { once: true });
        document.addEventListener('keydown', unlockAudio, { once: true });

        if (this.audioListener.context.state === 'running') {
            this.audioContextReady = true;
            this.userInteractionReceived = true;
        }
    }

    private playTestSound(): void {
        if (this.soundBuffers.size > 0) {
            const firstBuffer = this.soundBuffers.values().next().value;
            if (firstBuffer) {
                const testAudio = new PositionalAudio(this.audioListener);
                testAudio.setBuffer(firstBuffer);
                testAudio.setVolume(0.001);
                testAudio.play();
                setTimeout(() => testAudio.stop(), 100);
            }
        }
    }

    private canPlaySound(): boolean {
        if (!this.audioContextReady || !this.userInteractionReceived) {
            return false;
        }

        if (this.audioListener.context.state !== 'running') {
            console.warn('⚠️ AudioContext not running, attempting to resume...');
            this.audioListener.context.resume().catch((err) => {
                console.error('Failed to resume AudioContext:', err);
            });
            return false;
        }

        const now = Date.now();
        if (now - this.lastSecondTimestamp >= 1000) {
            this.lastSecondTimestamp = now;
            this.soundsPlayedThisSecond = 0;
        }

        if (this.soundsPlayedThisSecond >= this.MAX_SOUNDS_PER_SECOND) {
            return false;
        }

        return true;
    }

    public async preloadSounds(): Promise<void> {
        console.log('🔊 Preloading collision sounds...');

        const uniquePaths = [...new Set(this.soundConfigs.map((c) => c.soundPath))];
        const loadPromises = uniquePaths.map((path) => this.loadSound(path));

        try {
            await Promise.all(loadPromises);
            console.log(`✅ Loaded ${uniquePaths.length} collision sounds`);
        } catch (error) {
            console.warn('⚠️ Some collision sounds failed to load:', error);
        }
    }

    private loadSound(path: string): Promise<void> {
        return new Promise((resolve) => {
            this.audioLoader.load(
                path,
                (buffer) => {
                    this.soundBuffers.set(path, buffer);
                    this.audioPool.set(path, []);
                    resolve();
                },
                undefined,
                (error) => {
                    console.warn(`Failed to load sound: ${path}`, error);
                    resolve();
                },
            );
        });
    }

    private getAudioFromPool(soundPath: string): PositionalAudio | null {
        const buffer = this.soundBuffers.get(soundPath);
        if (!buffer) return null;

        let pool = this.audioPool.get(soundPath);
        if (!pool) {
            pool = [];
            this.audioPool.set(soundPath, pool);
        }

        let audio = pool.find((a) => !a.isPlaying);

        if (!audio && pool.length > 0) {
            audio = pool.find((a) => {
                if (a.source) {
                    //@ts-ignore
                    return !a.source.playbackRate || a.source.context.currentTime > 0;
                }
                return false;
            });

            if (!audio) {
                audio = pool[0];
                if (audio.isPlaying) {
                    audio.stop();
                }

                this.scene.remove(audio);
            }
        }

        if (!audio && pool.length < 10) {
            audio = new PositionalAudio(this.audioListener);
            audio.setBuffer(buffer);
            pool.push(audio);
        }

        if (!audio) {
            console.warn('🔴 Audio pool exhausted for:', soundPath.split('/').pop());
        }

        return audio || null;
    }

    public registerBody(body: Body, materialType: MaterialType, soundEnabled: boolean = true): void {
        //@ts-ignore
        if (!body.userData) body.userData = {};

        //@ts-ignore
        (body.userData as BodyMetadata).materialType = materialType;

        //@ts-ignore
        (body.userData as BodyMetadata).soundEnabled = soundEnabled;

        if (soundEnabled) {
            //@ts-ignore
            if (!(body.userData as any).hasCollisionListener) {
                body.addEventListener('collide', (event: any) => {
                    this.handleCollision(event.body, event.target, event);
                });

                //@ts-ignore
                (body.userData as any).hasCollisionListener = true;
            }
        }
    }

    private handleCollision(body1: Body, body2: Body, event: any): void {
        //@ts-ignore
        const meta1 = body1.userData as BodyMetadata;

        //@ts-ignore
        const meta2 = body2.userData as BodyMetadata;

        if (!meta1?.materialType || !meta2?.materialType) return;
        if (meta1.soundEnabled === false || meta2.soundEnabled === false) return;

        const impulse = this.calculateImpulse(event);
        if (impulse < this.MIN_IMPULSE) return;

        const collisionKey = this.getCollisionKey(body1, body2);
        const now = Date.now();
        const lastTime = this.lastCollisionTime.get(collisionKey) || 0;

        if (now - lastTime < this.COLLISION_COOLDOWN) return;

        this.lastCollisionTime.set(collisionKey, now);

        const soundConfig = this.findSoundConfig(meta1.materialType, meta2.materialType);
        if (!soundConfig) return;

        const collisionPoint = new Vector3()
            .copy(body1.position as any)
            .add(body2.position as any)
            .multiplyScalar(0.5);

        this.playCollisionSound(soundConfig, impulse, collisionPoint);
    }

    private calculateImpulse(event: any): number {
        if (event.contact && event.contact.getImpactVelocityAlongNormal) {
            return Math.abs(event.contact.getImpactVelocityAlongNormal());
        }
        return event.body?.velocity?.length() || 0;
    }

    private getCollisionKey(body1: Body, body2: Body): string {
        const id1 = body1.id;
        const id2 = body2.id;
        return id1 < id2 ? `${id1}-${id2}` : `${id2}-${id1}`;
    }

    private findSoundConfig(mat1: MaterialType, mat2: MaterialType): CollisionSoundConfig | null {
        return (
            this.soundConfigs.find(
                (config) =>
                    (config.material1 === mat1 && config.material2 === mat2) ||
                    (config.material1 === mat2 && config.material2 === mat1),
            ) || null
        );
    }

    private playCollisionSound(config: CollisionSoundConfig, impulse: number, position: Vector3): void {
        if (!this.canPlaySound()) {
            return;
        }

        const audio = this.getAudioFromPool(config.soundPath);
        if (!audio) {
            console.warn('🔴 No audio available from pool for:', config.soundPath.split('/').pop());
            return;
        }

        if (audio.isPlaying) {
            audio.stop();
        }

        if (audio.source) {
            audio.source.onended = null;
        }

        this.scene.remove(audio);

        if (this.activeSounds.size >= this.MAX_CONCURRENT_SOUNDS) {
            this.cleanupFinishedSounds();
            if (this.activeSounds.size >= this.MAX_CONCURRENT_SOUNDS) {
                const oldestSound = this.activeSounds.values().next().value;
                if (oldestSound) {
                    oldestSound.stop();
                    this.activeSounds.delete(oldestSound);
                    this.scene.remove(oldestSound);
                }
            }
        }

        const baseVolume = config.volume || 0.5;
        const volumeMultiplier = Math.min(1.0, impulse / 2.0);
        const finalVolume = baseVolume * volumeMultiplier * 0.8;

        this.scene.add(audio);
        audio.position.copy(position);

        audio.setRefDistance(1);
        audio.setRolloffFactor(2);

        audio.setVolume(finalVolume);
        audio.setPlaybackRate(0.95 + Math.random() * 0.1);

        this.activeSounds.add(audio);
        this.soundsPlayedThisSecond++;

        try {
            audio.play();

            if (audio.source) {
                audio.source.onended = () => {
                    this.activeSounds.delete(audio);
                    this.scene.remove(audio);
                };
            }

            const duration = audio.buffer?.duration || 2;
            setTimeout(() => {
                if (audio.isPlaying) {
                    audio.stop();
                }
                this.activeSounds.delete(audio);
                this.scene.remove(audio);
            }, (duration + 0.5) * 1000);
        } catch (error) {
            console.warn('⚠️ Failed to play collision sound:', error);
            this.activeSounds.delete(audio);
            this.scene.remove(audio);
        }
    }

    private cleanupFinishedSounds(): void {
        const soundsToRemove: PositionalAudio[] = [];

        this.activeSounds.forEach((audio) => {
            if (!audio.isPlaying) {
                soundsToRemove.push(audio);
            }
        });

        soundsToRemove.forEach((audio) => {
            this.activeSounds.delete(audio);
            this.scene.remove(audio);
        });
    }

    public addSoundConfig(config: CollisionSoundConfig): void {
        this.soundConfigs.push(config);
    }

    public dispose(): void {
        if (this.cleanupInterval) {
            clearInterval(this.cleanupInterval);
            this.cleanupInterval = null;
        }

        this.activeSounds.forEach((audio) => {
            if (audio.isPlaying) {
                audio.stop();
            }
            if (audio.source) {
                audio.source.onended = null;
            }
            audio.disconnect();
            this.scene.remove(audio);
        });

        this.activeSounds.clear();
        this.soundBuffers.clear();
        this.audioPool.clear();
        this.lastCollisionTime.clear();
    }
}
